import { useForm } from 'react-hook-form';
import { useCreateClaim } from '@edison/api-client-react';
import { useLocation } from 'wouter';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button.js';

interface FormValues {
  name: string;
  incomeYear: string;
}

export function ClaimCreatePage() {
  const { mutate: createClaim, isPending, error } = useCreateClaim();
  const [, navigate] = useLocation();

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();

  const onSubmit = (values: FormValues) => {
    createClaim(values, {
      onSuccess: (res) => {
        navigate(`/claims/${res.data.id}`);
      },
    });
  };

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">New claim</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create a new R&DTI claim for an income year.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Failed to create claim. Please try again.</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium">
            Claim name <span className="text-destructive">*</span>
          </label>
          <input
            id="name"
            type="text"
            placeholder="e.g. FY2025 R&D Claim"
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            {...register('name', { required: 'Claim name is required' })}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="incomeYear" className="block text-sm font-medium">
            Income year <span className="text-destructive">*</span>
          </label>
          <input
            id="incomeYear"
            type="text"
            placeholder="FY2025"
            className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            {...register('incomeYear', {
              required: 'Income year is required',
              pattern: { value: /^FY\d{4}$/, message: 'Must be in format FY followed by 4 digits (e.g. FY2025)' },
            })}
          />
          {errors.incomeYear && (
            <p className="text-xs text-destructive">{errors.incomeYear.message}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Must match the income year of the projects in this claim (e.g. FY2025).
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? 'Creating…' : 'Create claim'}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/claims')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
